// screenTimeUsage.js — THE reconstruction of the full-day usage number from
// a screen_time_days row. Written once, shared: the Health-tab usage card
// imports it directly, and api/notify-cron.js / api/coach.js import it via
// relative path (pure ESM, no JSX — Vercel's bundler traces it). Do not
// fork a server copy; this file IS both halves.
//
// The number is a STEPPED FLOOR, never an exact total: DeviceActivity
// delivers threshold crossings, not minutes (Apple's exact-total API is
// sandboxed and cannot export). usage_steps_fired is the highest delivered
// 30-min step index, clamped at write by the extension's wall-clock rule;
// this function re-applies the same clamp at read (defense in depth: bad
// data already written, clock skew, a false fire that slipped the write
// clamp before the extension shipped it).

/**
 * @param row screen_time_days row ({ armed_at, usage_armed_at, closed_at,
 *   usage_steps_fired, usage_step_minutes }) or an App-Group day record mapped
 *   to those names.
 * @returns { minutes, asOfLabel } — or null when the ladder was never armed
 *   for this day (usage_step_minutes null = unknown, NEVER zero).
 *   minutes 0 with a label is the honest "armed, under one step so far".
 */
export function reconstructScreenTimeUsage(row, nowMs = Date.now()) {
  if (!row || !row.usage_step_minutes) return null;
  const step = Number(row.usage_step_minutes);
  if (!step || step <= 0) return null;
  const steps = Number(row.usage_steps_fired) || 0;
  const raw = steps * step;
  // Clamp window: usage_armed_at → closed_at (finished day) or now (open day).
  // Same rule as the extension's write-side clamp, one step of slack. Base is
  // the USAGE window's own start (usage/cap split) so a cap re-arm never moves
  // it; falls back to armed_at for pre-split rows.
  let minutes = raw;
  let overcount = false;
  const usageArmedAt = row.usage_armed_at || row.armed_at;
  if (usageArmedAt) {
    const endMs = row.closed_at ? Date.parse(row.closed_at) : nowMs;
    const elapsedMin = Math.max(0, (endMs - Date.parse(usageArmedAt)) / 60000);
    const ceiling = Math.floor(elapsedMin) + step;
    minutes = Math.min(raw, ceiling);
    // Data-sanity: a floor is an "at least", so it must never exceed the time
    // actually elapsed since the window opened. If the raw stepped count is more
    // than a full extra step past that ceiling, a false threshold fire slipped
    // the write clamp (iOS 26 has documented false fires) — the number is not
    // trustworthy. Flag it so the caller shows a recheck state instead of
    // asserting an impossible total, rather than silently displaying the clamp.
    if (raw > ceiling + step) overcount = true;
  }
  return {
    minutes,
    overcount,
    asOfLabel: `as of the last ${step}-min mark`,
  };
}

// Is a recorded cap_crossed_at physically possible? A DeviceActivity threshold
// for an N-minute cap can only fire after N minutes of screen use, which needs
// at least N minutes of WALL CLOCK since the cap window armed. iOS 26 has
// documented premature-fire bursts (Apple forums 811305 / 812472): on
// 2026-09-29 an 8h (480-min) cap stamped cap_crossed_at 21 minutes after arm,
// with midpoint and the same-second usage step — every threshold fired at once.
// Returns true when the crossing could be real, false when it is an impossible
// burst. Same wall-clock rule the usage ladder already clamps by, one step of
// slack for arm/crossing clock skew. No numeric cap or no arm time = cannot
// check = trust the event (errs toward showing crossed, the safe direction for
// a limit the user set).
export function capCrossPlausible(row) {
  if (!row || !row.cap_crossed_at) return false;
  const capMin = Number(row.cap_minutes) || 0;
  if (capMin <= 0) return true;
  const crossedMs = Date.parse(row.cap_crossed_at);
  if (!Number.isFinite(crossedMs)) return true;
  // The arm to measure from is the earliest arm of the day that PRECEDES the
  // crossing. armed_at / cap_armed_at re-stamp on every window open (re-arm
  // honesty, 2026-09-16), so an app open after a real crossing moved
  // cap_armed_at past cap_crossed_at and this read a negative elapsed time
  // as "impossible": Oct 14 crossed at 4:12 PM, re-armed at 9:45 PM, and the
  // day read "partial" instead of "over" (2026-10-15). usage_armed_at holds
  // the day's first arm.
  // Measure from the CAP window's own arm when it precedes the crossing (a
  // burst minutes after a mid-day re-arm must still be caught); only when
  // that arm was re-stamped AFTER the crossing (app opened later that night)
  // fall back to the earliest arm of the day that precedes it.
  const capArm = Date.parse(row.cap_armed_at || row.armed_at || '');
  const arms = [row.cap_armed_at, row.armed_at, row.usage_armed_at]
    .map(a => (a ? Date.parse(a) : NaN)).filter(Number.isFinite);
  if (!arms.length) return true;
  const before = arms.filter(a => a <= crossedMs);
  const armedMs = Number.isFinite(capArm) && capArm <= crossedMs ? capArm
    : before.length ? Math.min(...before) : Math.min(...arms);
  const elapsedMin = (crossedMs - armedMs) / 60000;
  const slack = Number(row.usage_step_minutes) || 30;
  if (elapsedMin >= capMin - slack) return true;
  // A crossing that came "too soon" after the cap window's arm is still
  // real when the usage LADDER (counted from the usage window, which a cap
  // re-arm never resets) stands within a step of the cap: the native re-arm
  // sets the cap threshold to cap minus known usage (2026-10-17), so the
  // daemon's crossing lands long before cap-minutes of wall clock since the
  // re-arm. The ladder is the proof; the wall clock is not.
  const usage = reconstructScreenTimeUsage(row, Math.max(crossedMs, Date.parse(row.closed_at || '') || crossedMs));
  return !!usage && !usage.overcount && usage.minutes >= capMin - slack;
}

// The usage LADDER as proof of a crossing (2026-10-17): the ladder counts from
// the usage window's start and survives a cap edit, while a mid-day cap edit
// re-arms the CAP window and its threshold only accumulates usage from that
// moment. Oct 13: cap edited 8h → 5h at 9:08 PM after ~8.5h of use; the 5h
// threshold could never fire in the 3h left, so the day read "under". A
// clamped, non-overcounted ladder at or past the cap is usage at or past
// the cap, whatever the cap window saw.
export function ladderAtCap(row, nowMs = Date.now()) {
  const cap = Number(row?.cap_minutes) || 0;
  if (cap <= 0) return false;
  const usage = reconstructScreenTimeUsage(row, nowMs);
  return !!usage && !usage.overcount && usage.minutes >= cap;
}

// THE screen-time day-status derivation — the one place a day becomes
// over / under / partial / unknown. The cron's signal, the YESTERDAY block,
// the screen-cap adherence score and the client resolver all call this.
//   over:    a plausible cap_crossed_at, or the usage ladder at/past the cap.
//   under:   closed_at recorded, no crossing, and the CAP window opened within
//            15 minutes of local midnight (chained windows open at 00:00:30).
//   partial: closed cleanly, no crossing, armed mid-day — usage before arming
//            was unseen, so NEVER a confirmed under-day.
//   unknown: no close record, or a cap the daemon was not watching.
export function screenTimeDayStatus(row, zone, nowMs = Date.now()) {
  if (!row) return 'unknown';
  if (row.cap_crossed_at && capCrossPlausible(row)) return 'over';
  if (ladderAtCap(row, nowMs)) return 'over';
  if (!Number(row.cap_minutes)) return 'unknown';
  if (!row.closed_at) return 'unknown';
  const capArmedAt = row.cap_armed_at || row.armed_at;
  if (!capArmedAt || !zone) return 'partial';
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const parts = fmt.formatToParts(new Date(capArmedAt));
  const get = (type) => parts.find(p => p.type === type)?.value;
  const armedDate = `${get('year')}-${get('month')}-${get('day')}`;
  const armedMinutes = (Number(get('hour')) % 24) * 60 + Number(get('minute'));
  const fullCoverage = armedDate === row.date && armedMinutes <= 15;
  return fullCoverage ? 'under' : 'partial';
}

/** "2h 30m" / "45m" / "0m" — display form for the reconstructed floor. */
export function formatUsageMinutes(minutes) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}
